import { Typography, Container, Card, CardContent, Button, Box, Chip, useTheme, CircularProgress, Tabs, Tab } from "@mui/material"
import { styled, keyframes } from "@mui/material/styles"
import {
    Restaurant as RestaurantIcon,
    NotificationsActive as ReminderIcon,
    NotificationsActive as NotificationsActiveIcon,
    Cabin as CabinIcon,
    MenuBook as JournalIcon,
    Launch as LaunchIcon,
    ManageAccounts as ManageAccountsIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import useDeviceDetection from "../utils/useDeviceDetection"
import { useUserContext } from "../context/UserContext"
import { useEffect, useState } from "react";
import { GetBulletins, BulletinItem } from "../utils/BulletinBoardAPIRequest";
import CollapsibleBulletinBoardWeb from "../components/CollapsibleBulletinBoardWeb";
import {
    ThemeProvider,
    CssBaseline,
    IconButton,
    Badge,
    Fab,
    Grid,
    Paper,
    Avatar,
} from "@mui/material"
import {
    Brightness4,
    Brightness7,
    Notifications,
    Restaurant,
    MeetingRoom,
    Book,
    Add,
    People,
    Schedule,
} from "@mui/icons-material"
import LunchPollApp from "./LunchPollApp"
import CabinBookingPage from "./CabinBooking"
import JournalListing from "../components/JournalListing"
import BulletinBoardWeb from "../components/BulletinBoardAdmin/BulletinBoardWeb"
import EmployeeJournal from "./EmployeeJournal"

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    )
}
/* ---------- 2. ANIMATIONS ---------- */
const fadeInUp = keyframes`
  from { opacity:0; transform:translateY(30px); }
  to   { opacity:1; transform:translateY(0); }
`
const float = keyframes`
  0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)}
`

/* ---------- 3. STYLED HELPERS ---------- */
const AnimatedContainer = styled(Container)`
  animation: ${fadeInUp} .8s ease-out;
`
const FeatureCard = styled(Card) <{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  height: 350px; /* Fixed height for all cards */
  width: 300px; /* Fixed width for all cards */
  position: relative;
  overflow: hidden;
  transition: all .35s ease;
  display: flex;
  flex-direction: column;
  ${({ disabled }) => disabled && "opacity:.65; filter:grayscale(.4);"}
  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-10px)")};
    box-shadow: ${({ disabled }) => (disabled ? "none" : "0 20px 40px rgba(62, 80, 158, 0.25)")};
  }
`
const IconWrap = styled(Box) <{ bg: string }>`
  width: 80px; height: 80px; border-radius: 20px;
  display:flex;align-items:center;justify-content:center;
  background: ${({ bg }) => bg};
  margin: 0 auto 24px;
  flex-shrink: 0;
`
const ComingSoon = styled(Chip)`
  position:absolute; top:16px; right:16px;
  background: linear-gradient(135deg,#B8860B,#8B6914); color:#fff;
  font-weight:600; animation:${float} 3s infinite ease-in-out;
`
const GradientHeader = styled(Box)`
  background: linear-gradient(135deg,#7A9E3E 0%,#5A7A2E 100%);
`

/* -- Logos (white-tinted via CSS filter) -- */
const Logo = styled("img")`
  height:40px; width:auto; object-fit:contain; filter:brightness(0) invert(1);
`
const LogoDigital = styled(Logo)`
  height:35px;               /* a bit smaller for visual balance */
`

/* ---------- CARDS CONTAINER ---------- */
const CardsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 32px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`

/* ---------- 4. PAGE ---------- */
export default function HomeScreen() {
    const navigate = useNavigate();
    const { isMobile, isTablet, isDesktop } = useDeviceDetection();
    const theme = useTheme();
    // Use user context instead of localStorage
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isAdmin, currentUser } = useUserContext();

    // Bulletin state for web view
    const [bulletinMessages, setBulletinMessages] = useState<BulletinItem[]>([]);
    const [bulletinLoading, setBulletinLoading] = useState(true);
    const [bulletinError, setBulletinError] = useState<string | null>(null);

    const [tabValue, setTabValue] = useState(0)


    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue)
    }

    useEffect(() => {
        const fetchBulletins = async () => {
            setBulletinLoading(true);
            setBulletinError(null);
            try {
                const data = await GetBulletins();
                setBulletinMessages(data);
            } catch (e) {
                setBulletinError("Failed to load bulletins");
            } finally {
                setBulletinLoading(false);
            }
        };
        fetchBulletins();
    }, []);

    // All features
    const allFeatures = [
        ...((isAdmin || (currentUser && currentUser.lunchOpted)) ? [{
            id: "lunch",
            title: "Lunch Poll",
            desc: "Participate in today's lunch poll and help decide what everyone will enjoy together.",
            icon: <RestaurantIcon sx={{ fontSize: 40, color: theme.palette.primary.contrastText }} />,
            grad: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})`,
            href: "/lunch-poll",
            soon: false
        }] : []),
        {
            id: "remind",
            title: "Note To Self",
            desc: "Never miss a thought. Save notes in the form of text, voice or image - and send them to your inbox!",
            icon: <ReminderIcon sx={{ fontSize: 40, color: theme.palette.secondary.contrastText }} />,
            grad: `linear-gradient(135deg,${theme.palette.secondary.main},${theme.palette.secondary.dark})`,
            href: "/noteToSelf",
            soon: false
        },
        {
            id: "cabin",
            title: "Let's Meet",
            desc: "Reserve meeting rooms and cabin spaces for your team meetings and private calls.",
            icon: <CabinIcon sx={{ fontSize: 40, color: theme.palette.primary.contrastText }} />,
            grad: `linear-gradient(135deg,${theme.palette.primary.light},${theme.palette.primary.main})`,
            href: "/cabin-booking",
            soon: false,
        },
        {
            id: "journal",
            title: "Journal",
            desc: "Keep track of daily thoughts, work progress, and team collaboration notes.",
            icon: <JournalIcon sx={{ fontSize: 40, color: "#FFF" }} />,
            grad: `linear-gradient(135deg,${theme.palette.primary.light},${theme.palette.secondary.dark})`,
            href: "/employee-journal",
            soon: false,
        },
    ];

    // Show only User Management and Lunch Poll for admin
    let features = [];
    if (isAdmin) {
        features = [
            {
                id: "user-management",
                title: "User Management",
                desc: "Manage users, roles, and permissions for your organization.",
                icon: <ManageAccountsIcon sx={{ fontSize: 40, color: "#FFF" }} />,
                grad: `linear-gradient(135deg,${theme.palette.primary.light},${theme.palette.primary.main})`,
                href: "/user-management",
                soon: false,
            },
            {
                id: "bulletin-board",
                title: "Bulletin Board",
                desc: "Create, edit, and manage bulletin board items for your organization.",
                icon: <NotificationsActiveIcon sx={{ fontSize: 40, color: theme.palette.secondary.contrastText }} />,
                grad: `linear-gradient(135deg,${theme.palette.secondary.main},${theme.palette.secondary.dark})`,
                href: "/bulletin-board-admin",
                soon: false,
            },
            {
                id: "journals",
                title: "Journals",
                desc: "View and export all journals within the organization.",
                icon: <JournalIcon sx={{ fontSize: 40, color: theme.palette.primary.contrastText }} />,
                grad: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})`,
                href: "/journals",
                soon: false
            },
            allFeatures.find(f => f.id === "lunch")
        ].filter(Boolean);
    } else {
        features = allFeatures;
    }

    const open = (f: any) => {
        if (f.soon) return;
        // Navigate to internal routes for other features
        navigate(f.href);

    };

    return (
        // <Box
        //     sx={{
        //         py: 8,
        //         display: "flex",
        //         flexDirection: "column",
        //         justifyContent: "center",
        //     }}
        // >
        //     <AnimatedContainer maxWidth="lg">
        //         <Typography
        //             variant="h4"
        //             align="center"
        //             sx={{
        //                 mb: 2,
        //                 WebkitBackgroundClip: "text",
        //             }}
        //         >
        //             Welcome to ThoughtDesk
        //         </Typography>
        //         <Typography align="center" color="text.secondary" sx={{ mb: 6 }}>
        //             Access all your organizational tools in one place
        //         </Typography>

        //         {/* ---------- BULLETIN BOARD WEB VIEW ---------- */}
        //         {!isAdmin && (
        //             bulletinLoading ? (
        //                 <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        //                     <CircularProgress />
        //                 </Box>
        //             ) : bulletinError ? (
        //                 <Typography color="error" align="center" sx={{ mb: 4 }}>{bulletinError}</Typography>
        //             ) : (
        //                 <CollapsibleBulletinBoardWeb bulletinMessages={bulletinMessages} />
        //             )
        //         )}

        //         {/* ---------- FLEXBOX CARDS LAYOUT (NO GRID) ---------- */}
        //         <CardsContainer>
        //             {features.map((f, i) => {
        //                 if (!f) return null;
        //                 return (
        //                     <FeatureCard key={f.id} disabled={f.soon} onClick={() => open(f)}>
        //                         {f.soon && <ComingSoon label="Coming Soon" size="small" />}
        //                         <CardContent
        //                             sx={{
        //                                 p: 4,
        //                                 textAlign: "center",
        //                                 height: "100%",
        //                                 display: "flex",
        //                                 flexDirection: "column",
        //                                 justifyContent: "space-between",
        //                             }}
        //                         >
        //                             <Box>
        //                                 <IconWrap bg={f.grad}>{f.icon}</IconWrap>
        //                                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        //                                     {f.title}
        //                                 </Typography>
        //                                 <Typography
        //                                     color="text.secondary"
        //                                     sx={{
        //                                         mb: 3,
        //                                         minHeight: "48px",
        //                                         display: "flex",
        //                                         alignItems: "center",
        //                                         justifyContent: "center",
        //                                     }}
        //                                 >
        //                                     {f.desc}
        //                                 </Typography>
        //                             </Box>
        //                             <Button
        //                                 variant="contained"
        //                                 fullWidth
        //                                 disabled={f.soon}
        //                                 endIcon={!f.soon ? <LaunchIcon /> : undefined}
        //                                 sx={{
        //                                     background: f.soon ? "#cbd5e1" : f.grad,
        //                                     "&:hover": { background: f.soon ? "#cbd5e1" : f.grad },
        //                                     mt: "auto",
        //                                 }}
        //                             >
        //                                 {f.soon ? "Coming Soon" : "Open"}
        //                             </Button>
        //                         </CardContent>
        //                     </FeatureCard>
        //                 );
        //             })}
        //         </CardsContainer>
        //     </AnimatedContainer>
        // </Box>
        <Box
            sx={{
                flex: 1,
                minHeight: "100vh",
                background: theme.palette.background.default,
                position: "relative",
                width: "100%",
            }}
        >
            <Box py={4}>
                {/* Navigation Tabs */}
                <Paper
                    sx={{
                        background: theme.palette.background.default,
                        backdropFilter: "blur(20px)",
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: "hidden", // Add this to contain the indicator
                        pointerEvents: "auto",
                    }}
                >
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            "& .MuiTab-root": {
                                fontWeight: 600,
                                textTransform: "none",
                                fontSize: "1rem",
                                "&.Mui-selected": {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                                },
                            },
                            "& .MuiTabs-indicator": {
                                height: 3,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                borderRadius: "3px 3px 0 0",
                            },
                        }}
                    >
                        <Tab
                            id="tab-0"
                            aria-controls="tabpanel-0"
                            icon={
                                <Badge badgeContent={3} color="error">
                                    <Notifications />
                                </Badge>
                            }
                            label="Bulletin Board"
                            iconPosition="start"
                        />
                        <Tab
                            id="tab-1"
                            aria-controls="tabpanel-1"
                            icon={<Restaurant />}
                            label="Lunch Poll"
                            iconPosition="start"
                        />
                        <Tab
                            id="tab-2"
                            aria-controls="tabpanel-2"
                            icon={<MeetingRoom />}
                            label="Let's Meet"
                            iconPosition="start"
                        />
                        <Tab id="tab-3" aria-controls="tabpanel-3" icon={<Book />} label="Journals" iconPosition="start" />
                    </Tabs>
                </Paper>

                {/* Tab Content */}
                <TabPanel value={tabValue} index={0}>
                    {bulletinLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : bulletinError ? (
                        <Typography color="error" align="center" sx={{ mb: 4 }}>{bulletinError}</Typography>
                    ) : (
                        <BulletinBoardWeb bulletinMessages={bulletinMessages} />
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    <LunchPollApp />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    <CabinBookingPage />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                    <EmployeeJournal />
                </TabPanel>
            </Box>
        </Box>
    );
}